import type { RrpCalculationType, SimulationSettingsType } from '@/api';
import type { RmCalculationType } from '@/api/models/RmCalculationType';
import { FullSizeSpinner } from '@/components/full-size-spinner';
import { Flex, Text } from '@lego/klik-ui';
import { useGetUploadTypes } from '../calculation-settings-queries';
import FileUploadCard from './file-upload-card';

type BannerContent = {
  type: 'success' | 'negative';
  title: string;
  detailsUrl?: string | null;
  message?: string | null;
};

const labels: Record<
  RmCalculationType | RrpCalculationType | SimulationSettingsType,
  { title: string; description: string }
> = {
  Total: {
    title: 'Total',
    description: 'Price List ID, Price List Description, Year, RM%',
  },
  PerTierAndTheme: {
    title: 'Per tier and theme',
    description:
      'Price List ID, Price List Description, Year, Theme ID, Theme Description, Price Tier, Price Tier Description, RM%',
  },
  PerTierAndLicenseMark: {
    title: 'Per tier and license mark',
    description:
      'Price List ID, Price List Description, Year, License Mark, Price Tier, Price Tier Description, RM%',
  },
  CopyLocalLeadRmToLocalRm: {
    title: 'LEAD to Local',
    description: 'Price List ID, Price List Description, Year, LL RM%, RM%',
  },
  PerTierAndGroup: {
    title: 'Per tier and group',
    description:
      'Price List ID, Price List Description, Year, Theme ID, Theme Description, Group ID, Group Description, Price Tier, Price Tier Description, RM%',
  },
  LocalPricePointRounding: {
    title: 'Local price points',
    description:
      'Price List ID, Price List Description, Year, Currency, RRP Price Point',
  },
  PredefinedLeadToLocalPp: {
    title: 'Predefined LEAD to local PP',
    description:
      'Price List ID, Price List Description, Year, Currency, LL RRP Price Point, RRP Price Point',
  },
  CalculationSettings: {
    title: 'Targets & Thresholds',
    description:
      'Price List ID, Price List Description, Year, Target LP Index, LP/FMC Target, Price Threshold, Above price threshold min, Above price threshold max, Below price threshold min, Below price threshold max',
  },
};

const RulesContent = () => {
  const { data: calculationTypes, isFetching } = useGetUploadTypes();

  const categories = [
    {
      id: 'rm',
      title: 'RM Calculation Methods',
      cards:
        calculationTypes?.rmCalculationTypes
          ?.filter((type) => type.target !== null)
          .map((type) => ({
            id: type.target as string,
            title: labels[type.type].title,
            description: labels[type.type].description,
          })) ?? [],
    },
    {
      id: 'rrp',
      title: 'RRP Calculation Methods',
      cards:
        calculationTypes?.rrpCalculationTypes
          ?.filter((type) => type.target !== null)
          .map((type) => ({
            id: type.target as string,
            title: labels[type.type].title,
            description: labels[type.type].description,
          })) ?? [],
    },
    {
      id: 'sim',
      title: 'Simulation settings',
      cards:
        calculationTypes?.simulationSettings
          ?.filter((type) => type.target !== null)
          .map((type) => ({
            id: type.target as string,
            title: labels[type.type].title,
            description: labels[type.type].description,
          })) ?? [],
    },
  ];

  return (
    <>
      {isFetching ? (
        <FullSizeSpinner />
      ) : (
        <Flex
          flexDirection="column"
          gap={8}
          padding={8}
          maxWidth="1352px"
          margin="auto"
        >
          {categories.map((category) => (
            <Flex key={category.id} flexDirection="column" gap={4}>
              <Text
                fontSize="2xl"
                fontWeight="bold"
                marginTop={4}
                marginBottom={2}
                alignItems="flex-start"
              >
                {category.title}
              </Text>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
                  gap: '24px',
                  maxWidth: '1128px' /* 3 × 360px + 2 × 24px */,
                }}
              >
                {category.cards.map((card) => (
                  <FileUploadCard
                    key={card.id}
                    target={card.id}
                    content={{
                      title: card.title,
                      description: card.description,
                    }}
                  />
                ))}
              </div>
            </Flex>
          ))}
        </Flex>
      )}
    </>
  );
};

export default RulesContent;
